import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Alert, FormGroup, Label, Input, Nav, NavItem, NavLink,
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const getAuthHeaders = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const DURUM_RENK = {
  Beklemede: "warning",
  Onaylandi: "success",
  Reddedildi: "danger",
};

const DURUM_ETIKET = {
  Beklemede: "Beklemede",
  Onaylandi: "Onaylandı",
  Reddedildi: "Reddedildi",
};

/**
 * Hizmet'eGEL — "Onaylı Sağlayıcı" rozeti başvuruları.
 *
 * NEDEN BU SAYFA: api/Hizmet/OnayBasvuru mobilden başvuru alıyor ve adminlere
 * bildirim gönderiyordu, ama sonuçlandırma ekranı hiç yazılmamıştı. Başvurular
 * "Beklemede" durumunda birikiyor, kullanıcı cevap alamıyordu.
 *
 * Onaylandığında KULLANICILAR.ONAYLANDI = true olur; haritada ve tekliflerde
 * kalkan rozeti görünür (sunucu tarafında yapılır, burada tetiklenir).
 */
const HizmetOnayBasvurulari = () => {
  document.title = "Onaylı Sağlayıcı Başvuruları | HarmanaGEL";

  const [basvurular, setBasvurular] = useState([]);
  const [aktifSekme, setAktifSekme] = useState("Beklemede");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [detailModal, setDetailModal] = useState(false);
  const [redModal, setRedModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [retSebebi, setRetSebebi] = useState("");
  const [islemLoading, setIslemLoading] = useState(null);

  const fetchBasvurular = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // OData'da HIZMET_ONAY_BASVURULARI EntitySet'i yok (404) — custom uç
      // hem listeyi hem başvuranın profilini tek çağrıda döndürüyor.
      const res = await fetch(`${BASE_URL}/api/Hizmet/OnayBasvurulari`, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401) throw new Error("Bu sayfa için admin yetkisi gerekiyor.");
      if (!res.ok) throw new Error(`Başvurular alınamadı (${res.status})`);
      const json = await res.json();
      setBasvurular(json?.Basvurular || []);
    } catch (e) {
      setError(e.message || "Bir hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchBasvurular(); }, []);

  const sonuclandir = async (basvuruId, onayla, sebep) => {
    setIslemLoading(basvuruId);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/api/Hizmet/OnaySonuclandir`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          BasvuruId: basvuruId,
          Onayla: onayla,
          RetSebebi: onayla ? null : (sebep || null),
        }),
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text?.slice(0, 200) || "İşlem başarısız.");

      setSuccessMsg(onayla
        ? "Başvuru onaylandı. Kullanıcı artık haritada onaylı rozetiyle görünecek."
        : "Başvuru reddedildi. Kullanıcıya bildirim gönderildi.");
      setTimeout(() => setSuccessMsg(""), 4000);
      setRedModal(false);
      setDetailModal(false);
      setRetSebebi("");
      fetchBasvurular();
    } catch (e) {
      setError(e.message);
    } finally {
      setIslemLoading(null);
    }
  };

  const filtreli = useMemo(
    () => basvurular.filter((b) => b.DURUM === aktifSekme),
    [basvurular, aktifSekme]
  );

  const sayac = useMemo(() => ({
    Beklemede: basvurular.filter((b) => b.DURUM === "Beklemede").length,
    Onaylandi: basvurular.filter((b) => b.DURUM === "Onaylandi").length,
    Reddedildi: basvurular.filter((b) => b.DURUM === "Reddedildi").length,
  }), [basvurular]);

  const tarihYaz = (d) => {
    if (!d) return "-";
    // API UTC ("Z") döndürüyor; tarayıcı yerel saate çevirir.
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? "-" : dt.toLocaleString("tr-TR");
  };

  const columns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <span className="fw-bold">#{cell.getValue()}</span>,
    },
    {
      header: "Başvuran",
      accessorKey: "KULLANICI_ID",
      enableSorting: false,
      enableColumnFilter: true,
      cell: (cell) => {
        const b = cell.row.original;
        return (
          <div className="d-flex align-items-center">
            <img
              src={b.ProfilFoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(b.AdSoyad || "K")}&background=random`}
              alt=""
              className="rounded-circle me-2"
              style={{ width: 34, height: 34, objectFit: "cover" }}
            />
            <div>
              <div className="fw-bold">{b.AdSoyad || "Kullanıcı"}</div>
              <small className="text-muted">{b.Telefon || b.Eposta || `#${b.KULLANICI_ID}`}</small>
            </div>
          </div>
        );
      },
    },
    {
      header: "Hizmet Alanı",
      accessorKey: "KULLANICI_ID_sektor",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const ad = cell.row.original.SektorAd;
        return ad
          ? <Badge color="info" className="font-size-12">{ad}</Badge>
          : <span className="text-muted">-</span>;
      },
    },
    {
      header: "Konum",
      accessorKey: "KULLANICI_ID_konum",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        // Konumu olmayan sağlayıcı haritada zaten görünmez — onay öncesi uyarı.
        return cell.row.original.Enlem
          ? <Badge color="success" className="font-size-11">Konum var</Badge>
          : <Badge color="danger" className="font-size-11">Konum yok</Badge>;
      },
    },
    {
      header: "Başvuru Tarihi",
      accessorKey: "BASVURU_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => tarihYaz(cell.getValue()),
    },
    {
      header: "Durum",
      accessorKey: "DURUM",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color={DURUM_RENK[cell.getValue()] || "secondary"} className="font-size-12">
          {DURUM_ETIKET[cell.getValue()] || cell.getValue()}
        </Badge>
      ),
    },
    {
      header: "İşlem",
      accessorKey: "islem",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const b = cell.row.original;
        const yukleniyor = islemLoading === b.ID;
        return (
          <div className="d-flex gap-1">
            <Button
              size="sm"
              color="light"
              onClick={() => { setSelected(b); setDetailModal(true); }}
            >
              <i className="bx bx-show" /> Detay
            </Button>
            {b.DURUM === "Beklemede" && (
              <>
                <Button
                  size="sm"
                  color="success"
                  disabled={yukleniyor}
                  onClick={() => sonuclandir(b.ID, true)}
                >
                  {yukleniyor ? <Spinner size="sm" /> : <><i className="bx bx-check" /> Onayla</>}
                </Button>
                <Button
                  size="sm"
                  color="danger"
                  disabled={yukleniyor}
                  onClick={() => { setSelected(b); setRetSebebi(""); setRedModal(true); }}
                >
                  <i className="bx bx-x" /> Reddet
                </Button>
              </>
            )}
          </div>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [islemLoading]);

  // Başvuru kaydı başvuranın profilini de taşıyor (tek çağrı).
  const seciliKullanici = selected;

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Hizmet'eGEL" breadcrumbItem="Onaylı Sağlayıcı Başvuruları" />

          {error && <Alert color="danger">{error}</Alert>}
          {successMsg && <Alert color="success">{successMsg}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Nav tabs className="nav-tabs-custom mb-3">
                    {[
                      { k: "Beklemede", l: "Bekleyen", c: "warning" },
                      { k: "Onaylandi", l: "Onaylanan", c: "success" },
                      { k: "Reddedildi", l: "Reddedilen", c: "danger" },
                    ].map((t) => (
                      <NavItem key={t.k}>
                        <NavLink
                          style={{ cursor: "pointer" }}
                          className={aktifSekme === t.k ? "active" : ""}
                          onClick={() => setAktifSekme(t.k)}
                        >
                          {t.l}{" "}
                          {sayac[t.k] > 0 && (
                            <Badge color={t.c} pill className="ms-1">{sayac[t.k]}</Badge>
                          )}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>

                  {isLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : filtreli.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-shield-quarter" style={{ fontSize: 40 }} />
                      <p className="mt-2 mb-0">Bu durumda başvuru yok.</p>
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={filtreli}
                      isGlobalFilter={true}
                      SearchPlaceholder="Başvuru ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* DETAY */}
      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="lg">
        <ModalHeader toggle={() => setDetailModal(false)}>
          Başvuru Detayı {selected ? `#${selected.ID}` : ""}
        </ModalHeader>
        <ModalBody>
          {seciliKullanici ? (
            <Row>
              <Col md={4} className="text-center">
                <img
                  src={seciliKullanici.ProfilFoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(seciliKullanici.AdSoyad || "K")}&background=random&size=160`}
                  alt=""
                  className="rounded-circle mb-2"
                  style={{ width: 110, height: 110, objectFit: "cover" }}
                />
                <h5 className="mb-1">{seciliKullanici.AdSoyad}</h5>
                {seciliKullanici.ZatenOnayli && (
                  <Badge color="success">Zaten onaylı</Badge>
                )}
              </Col>
              <Col md={8}>
                <dl className="row mb-0">
                  <dt className="col-sm-4">Telefon</dt>
                  <dd className="col-sm-8">{seciliKullanici.Telefon || "-"}</dd>
                  <dt className="col-sm-4">E-posta</dt>
                  <dd className="col-sm-8">{seciliKullanici.Eposta || "-"}</dd>
                  <dt className="col-sm-4">Hizmet Alanı</dt>
                  <dd className="col-sm-8">{seciliKullanici.SektorAd || "-"}</dd>
                  <dt className="col-sm-4">Deneyim</dt>
                  <dd className="col-sm-8">{seciliKullanici.DeneyimYili ? `${seciliKullanici.DeneyimYili} yıl` : "-"}</dd>
                  <dt className="col-sm-4">Ekipman</dt>
                  <dd className="col-sm-8">{seciliKullanici.Ekipman || "-"}</dd>
                  <dt className="col-sm-4">Kapasite</dt>
                  <dd className="col-sm-8">{seciliKullanici.Kapasite || "-"}</dd>
                  <dt className="col-sm-4">Hizmet Bölgesi</dt>
                  <dd className="col-sm-8">{seciliKullanici.HizmetBolge || "-"}</dd>
                  <dt className="col-sm-4">Puan</dt>
                  <dd className="col-sm-8">{seciliKullanici.OrtalamaPuan ? Number(seciliKullanici.OrtalamaPuan).toFixed(1) : "Puan yok"}</dd>
                  <dt className="col-sm-4">Harita Konumu</dt>
                  <dd className="col-sm-8">
                    {seciliKullanici.Enlem ? (
                      <a
                        href={`https://www.google.com/maps?q=${seciliKullanici.Enlem},${seciliKullanici.Boylam}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Haritada gör
                      </a>
                    ) : (
                      <span className="text-danger">Konum yok — onaylansa da haritada görünmez</span>
                    )}
                  </dd>
                  <dt className="col-sm-4">Başvuru Tarihi</dt>
                  <dd className="col-sm-8">{tarihYaz(selected?.BASVURU_TARIHI)}</dd>
                  {selected?.ACIKLAMA && (
                    <>
                      <dt className="col-sm-4">Başvuru Notu</dt>
                      <dd className="col-sm-8">{selected.ACIKLAMA}</dd>
                    </>
                  )}
                  {selected?.BELGE_URL && (
                    <>
                      <dt className="col-sm-4">Belge</dt>
                      <dd className="col-sm-8">
                        <a href={selected.BELGE_URL} target="_blank" rel="noreferrer">Belgeyi aç</a>
                      </dd>
                    </>
                  )}
                  {selected?.RET_SEBEBI && (
                    <>
                      <dt className="col-sm-4">Ret Sebebi</dt>
                      <dd className="col-sm-8 text-danger">{selected.RET_SEBEBI}</dd>
                    </>
                  )}
                </dl>
              </Col>
            </Row>
          ) : (
            <p className="text-muted mb-0">Kullanıcı bilgisi yüklenemedi.</p>
          )}
        </ModalBody>
        <ModalFooter>
          {selected?.DURUM === "Beklemede" && (
            <>
              <Button
                color="danger"
                onClick={() => { setRetSebebi(""); setRedModal(true); }}
              >
                Reddet
              </Button>
              <Button
                color="success"
                disabled={islemLoading === selected?.ID}
                onClick={() => sonuclandir(selected.ID, true)}
              >
                {islemLoading === selected?.ID ? <Spinner size="sm" /> : "Onayla"}
              </Button>
            </>
          )}
          <Button color="secondary" onClick={() => setDetailModal(false)}>Kapat</Button>
        </ModalFooter>
      </Modal>

      {/* RET SEBEBİ */}
      <Modal isOpen={redModal} toggle={() => setRedModal(false)}>
        <ModalHeader toggle={() => setRedModal(false)}>Başvuruyu Reddet</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Ret sebebi (kullanıcıya bildirim olarak gider)</Label>
            <Input
              type="textarea"
              rows={4}
              value={retSebebi}
              onChange={(e) => setRetSebebi(e.target.value)}
              placeholder="Örn: Belgeleriniz okunamıyor, lütfen net bir fotoğrafla tekrar başvurun."
            />
            <small className="text-muted">
              Boş bırakılırsa kullanıcıya "Sebep: Belirtilmedi" yazılır.
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setRedModal(false)}>Vazgeç</Button>
          <Button
            color="danger"
            disabled={islemLoading === selected?.ID}
            onClick={() => sonuclandir(selected.ID, false, retSebebi)}
          >
            {islemLoading === selected?.ID ? <Spinner size="sm" /> : "Reddet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default HizmetOnayBasvurulari;
